import axios, { AxiosError } from 'axios'
import { gql } from '@apollo/client'
import { environment } from '@mortgage-pos/ui/env'
import { getCSRFToken } from '@mortgage-pos/utils/tokens'
import { borrowerPortalLoginUrl } from '@mortgage-pos/data'
import axiosRetry from 'axios-retry'
import {
  HttpLibrary,
  getHttpLibraryFromEnv,
  buildUrl,
  headersToRecord,
  parseFetchResponseBody,
  createFetchHttpError,
  prepareFetchInit,
  buildFetchInitWithTimeoutAndSignal,
  createAxiosCompatibleTimeoutError,
  isFetchTimeoutAbortError,
  isRetryableError,
  FetchClient,
  type FetchResponse,
  type FetchRequestConfig,
} from '@mortgage-pos/shared-http'

function getHttpLibrary(): HttpLibrary {
  return getHttpLibraryFromEnv(process.env.USE_FETCH_HTTP_CLIENT_ALFIE)
}

const axiosHttp = axios.create({
  withCredentials: true,
})

const axiosHttpBorrowerPortal = axios.create({
  withCredentials: true,
  baseURL: environment.borrowerPortal.apiBaseUrl,
})

export const cohesionHeaders = () => {
  const aliasSet = (window as any).tagular?.('getAliasSet')
  if (!aliasSet) {
    return {}
  }

  const returnHeaders = {
    'cohesion-anonymous-id': aliasSet.anonymousId,
    'cohesion-session-id': aliasSet.sessionId,
    'cohesion-instance-id': aliasSet.instanceId,
    'cohesion-tenant-id': aliasSet.tenantId,
    'cohesion-transit-id': aliasSet.transitId,
  }

  // @ts-ignore
  const webContext: WebContext = window?._Tagular?.webContext
  if (webContext) {
    return {
      ...returnHeaders,
      'webcontext-authenticate-user-id': webContext?.authenticatedUserId,
      'webcontext-from-manual': webContext?.fromManual,
      'webcontext-library-name': webContext?.library?.name,
      'webcontext-library-version': webContext?.library?.version,
      'webcontext-manual-instance-id': webContext?.manualInstanceId,
      'webcontext-page-referrer': webContext?.page?.referrer,
      'webcontext-page-title': webContext?.page?.title,
      'webcontext-page-url': webContext?.page?.url,
      'webcontext-page-revision-id': webContext?.page?.revisionId,
      'webcontext-page-page-type': webContext?.page?.pageType,
      'webcontext-page-post-id': webContext?.page?.postId,
      'webcontext-page-timezone-offset': webContext?.timezoneOffset,
      'webcontext-cross-site-id': webContext?.crossSiteId,
      'webcontext-hash-id': webContext?.hashId,
      'webcontext-cohesion-consent': webContext?.cohesionConsent,
      'webcontext-cookie-consent-essential':
        webContext?.cookieConsent.essential,
      'webcontext-cookie-consent-performance':
        webContext?.cookieConsent.performance,
      'webcontext-cookie-consent-preference':
        webContext?.cookieConsent.preference,
      'webcontext-cookie-consent-targeting':
        webContext?.cookieConsent.targeting,
      'webcontext-cookie-consent-social': webContext?.cookieConsent.social,
    }
  }

  return returnHeaders
}

async function runFetchRequest(
  baseUrl: string,
  method: string,
  path: string,
  config?: FetchRequestConfig,
  body?: any
): Promise<FetchResponse> {
  const mergedConfig: FetchRequestConfig = {
    ...config,
    headers: {
      ...config?.headers,
    },
  }

  const url = buildUrl(baseUrl, path, mergedConfig.params)
  const fetchInitBase = prepareFetchInit(
    method,
    (mergedConfig.headers ?? {}) as Record<string, string>,
    body,
    'include'
  )

  const { fetchInit, cancelTimeout, timeoutController, timeoutMs } =
    buildFetchInitWithTimeoutAndSignal(fetchInitBase, {
      timeout: mergedConfig.timeout,
      signal: mergedConfig.signal,
    })

  try {
    let response: Response
    try {
      response = await fetch(url, fetchInit)
    } catch (networkError: any) {
      if (
        isFetchTimeoutAbortError(networkError, timeoutController, timeoutMs)
      ) {
        throw createAxiosCompatibleTimeoutError(
          timeoutMs,
          mergedConfig
        ) as AxiosError
      }
      throw networkError
    }

    let data: any
    try {
      data = await parseFetchResponseBody(response, mergedConfig.responseType)
    } catch (parseError: any) {
      if (isFetchTimeoutAbortError(parseError, timeoutController, timeoutMs)) {
        throw createAxiosCompatibleTimeoutError(
          timeoutMs,
          mergedConfig
        ) as AxiosError
      }
      throw parseError
    }
    const fetchResponse: FetchResponse = {
      data,
      status: response.status,
      headers: headersToRecord(response.headers),
    }

    if (!response.ok) {
      throw createFetchHttpError(
        response.status,
        data,
        fetchResponse.headers,
        url
      )
    }

    return fetchResponse
  } finally {
    cancelTimeout()
  }
}

const isCheckAuthInUrl = (err: any) =>
  err?.response?.request?.responseURL
    ?.toLowerCase()
    .includes('check-authentication')

const isUnauthorizedResponse = (err: any) =>
  err.response?.status === 401 ||
  err.response?.data?.message?.toLowerCase().includes('unauthorized')

function handleBorrowerPortal401(err: any) {
  if (!isCheckAuthInUrl(err) && isUnauthorizedResponse(err)) {
    window.location.href =
      environment.productName === 'sage' ||
      environment.productName === 'sage-home-loans'
        ? borrowerPortalLoginUrl
        : window.location.origin
  }
  throw err
}

axiosHttpBorrowerPortal.interceptors.response.use(undefined, (err) => {
  handleBorrowerPortal401(err)
})

axiosRetry(axiosHttpBorrowerPortal, { retries: 0 })

const fetchHttp = new FetchClient('', runFetchRequest)
const fetchHttpBorrowerPortalRaw = new FetchClient(
  environment.borrowerPortal.apiBaseUrl ?? '',
  runFetchRequest
)

function wrapWith401Handler(client: FetchClient): FetchClient {
  const wrap =
    (method: keyof FetchClient) =>
    async (...args: any[]) => {
      try {
        return await (client[method] as Function).apply(client, args)
      } catch (err) {
        handleBorrowerPortal401(err)
        throw err
      }
    }
  return {
    get: wrap('get'),
    post: wrap('post'),
    put: wrap('put'),
    patch: wrap('patch'),
    delete: wrap('delete'),
  } as unknown as FetchClient
}

const fetchHttpBorrowerPortal = wrapWith401Handler(fetchHttpBorrowerPortalRaw)

async function fetchHttpBorrowerPortalWithRetry(
  method: string,
  endpoint: string,
  payload: Record<string, any>,
  config: FetchRequestConfig,
  retries = 3
): Promise<FetchResponse> {
  let lastErr: any
  for (let i = 0; i <= retries; i++) {
    try {
      if (method === 'get' || method === 'delete') {
        return await (fetchHttpBorrowerPortal as any)[method](endpoint, config)
      }
      return await (fetchHttpBorrowerPortal as any)[method](
        endpoint,
        payload,
        config
      )
    } catch (e: any) {
      lastErr = e
      if (i < retries && isRetryableError(e, method)) {
        await new Promise((r) => setTimeout(r, (i + 1) * 1000))
        continue
      }
      break
    }
  }
  if (lastErr?.response?.status === 401) {
    handleBorrowerPortal401(lastErr)
  }
  throw lastErr
}

const http =
  getHttpLibrary() === HttpLibrary.Fetch ? (fetchHttp as any) : axiosHttp

const httpBorrowerPortal =
  getHttpLibrary() === HttpLibrary.Fetch
    ? (fetchHttpBorrowerPortal as any)
    : axiosHttpBorrowerPortal

export default http

export { httpBorrowerPortal }

// Hit our API using GraphQL
export async function graphQL(
  payload: Record<string, any>,
  headers?: Record<string, any>
) {
  return graphQLRequest(environment.apiBaseUrl, payload, headers)
}

// Hit our API through VeryGoodSecurity proxy
export async function graphQLVGS(
  payload: Record<string, any>,
  headers?: Record<string, any>
) {
  return graphQLRequest(environment.vgsBaseUrl, payload, headers)
}

// Hit the Sage API using GraphQL (like from Bankrate Compare)
export async function graphQLSage(
  payload: Record<string, any>,
  headers?: Record<string, any>
) {
  return graphQLRequest(environment.sageApiBaseUrl, payload, headers)
}

async function graphQLRequest(
  baseUrl: string,
  payload: Record<string, any>,
  headers: Record<string, any> = {}
) {
  const graphQLAst = gql`
    ${payload.query}
  `
  const queryParam = graphQLAst.definitions
    .map((definition) => definition['name']['value'])
    .join(',')

  // @ts-ignore
  const referrer = window.document.referrer

  return http.post(`${baseUrl}/graphql?queries=${queryParam}`, payload, {
    headers: {
      ...headers,
      ...cohesionHeaders(),
      referrer,
    },
  })
}

export async function borrowerPortalRequest(
  endpoint: string,
  payload: Record<string, any> = {},
  headers: Record<string, any> = {},
  method = 'post'
) {
  const referrer = window.document.referrer
  const csrf = getCSRFToken()

  const requestConfig = {
    headers: {
      ...headers,
      ...cohesionHeaders(),
      referrer,
      csrf,
    } as Record<string, string>,
  }

  if (getHttpLibrary() === HttpLibrary.Fetch) {
    return fetchHttpBorrowerPortalWithRetry(
      method,
      endpoint,
      payload,
      requestConfig,
      3
    )
  }

  const axiosConfig = {
    headers: {
      ...headers,
      ...cohesionHeaders(),
      referrer,
      csrf,
    },
    'axios-retry': {
      retries: 3,
      retryDelay: (retryCount: number) => retryCount * 1000,
    },
  }

  switch (method) {
    case 'patch':
      return httpBorrowerPortal.patch(endpoint, payload, axiosConfig)
    case 'put':
      return httpBorrowerPortal.put(endpoint, payload, axiosConfig)
    case 'post':
      return httpBorrowerPortal.post(endpoint, payload, axiosConfig)
    case 'get':
      return httpBorrowerPortal.get(endpoint, axiosConfig)
    case 'delete':
      return httpBorrowerPortal.delete(endpoint, axiosConfig)
    default:
      throw new Error(`Unsupported method: ${method}`)
  }
}
